import { DateTime } from "luxon";
import React from "react";
import { Link } from "react-router-dom";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import { ListPageHeader, ResultsCard, computeNbPages } from "../../common/DataList";
import CERListFilters from "./CERListFilters/CERListFilters";
import useCERList from "./useCERList";

const COUNT_LABELS = { none: "Aucun contrat trouvé", one: "contrat trouvé", many: "contrats trouvés" };

const CERList = () => {
  const [state] = useStore();
  const { CER_DURATIONS, CER_GOAL_PATH_TYPES, CER_GOAL_STATUSES } = state.constants.items;
  const { cerList, cerListFilters, actions, isSearchingCerList, hasSearchedCerList } = useCERList();

  const getCERPathTypes = (goals) => {
    var pathTypes = new Set();
    goals.forEach((goal) => {
      pathTypes.add(goal.pathType);
    });

    return [...pathTypes];
  };

  const getCERGoalsProgress = (goals) => {
    const count = [...CER_GOAL_STATUSES.map((s) => ({ ...s, count: 0 }))];
    goals.forEach((goal) => {
      count.find((c) => c.id == goal.detail.status).count++;
    });

    return count;
  };

  const checkIsMinor = (birthdate) => {
    const dur = DateTime.local().diff(DateTime.fromISO(birthdate), "years").toObject();

    return dur.years < 18;
  };

  return (
    <div className="container-fluid px-5 my-5 list-page">
      <ListPageHeader title="Contrats CER" />
      <CERListFilters />
      <ResultsCard
        caption="Liste des contrats CER"
        rows={cerList.data}
        count={cerList.count}
        countLabels={COUNT_LABELS}
        hasSearched={hasSearchedCerList}
        isLoading={isSearchingCerList}
        page={cerListFilters.page}
        nbPages={computeNbPages(cerList.count, cerListFilters.nbItemsByPage)}
        nbItemsByPage={cerListFilters.nbItemsByPage}
        onChangePage={actions.changePage}
        onChangeNbItemsByPage={actions.changeNbItemsByPage}
        itemLabel="contrats"
        fields={[
          {
            path: "userRecipient",
            name: "Allocataire",
            render: (el) => (
              <Link to={`/recipients/${el.id}`}>
                {tools.getFullname(el)}
                <div>
                  <small>{el.nir}</small>
                </div>
                {checkIsMinor(el.birthdate) && <span className="badge badge-info">mineur</span>}
              </Link>
            ),
          },

          {
            path: "*",
            name: "Créé le",
            exportPath: "createdAt",
            render: (el) => (
              <>
                <div>{tools.formatDate(el.createdAt)}</div>
                <div>
                  <small>màj le {tools.formatDate(el.updatedAt)}</small>
                </div>
              </>
            ),
          },
          {
            path: "*",
            name: "Période",
            exportPath: "period",
            render: (el) =>
              `du ${tools.formatDate(el.beginAt)} au ${tools.formatDate(el.endAt)} - ${
                tools.findIn(CER_DURATIONS, "id", el.duration).name
              }`,
          },

          {
            path: "*",
            name: "Statut",
            exportPath: "status",
            render: (el) => {
              return el.status == "SIGNED" ? (
                <>
                  <div className="badge badge-success">SIGNÉ</div>
                  <div>
                    <small>1ere sign. : {tools.formatDate(el.signedAt)}</small>
                  </div>
                  <div>
                    <small>dern. sign. : {tools.formatDate(el.signUpdatedAt)}</small>
                  </div>
                </>
              ) : (
                <div className="badge badge-dark">BROUILLON</div>
              );
            },
          },
          {
            path: "goals",
            name: "Types",
            // Un type par ligne, dans l'ordre des constantes et avec une couleur
            // fixe par type, quel que soit l'ordre des objectifs du contrat.
            render: (el) => {
              const types = getCERPathTypes(el);
              return (
                <div className="cell-stack">
                  {CER_GOAL_PATH_TYPES.map((t, tk) =>
                    types.includes(t.id) ? (
                      <div className={`badge badge-${["primary", "info"][tk] || "secondary"}`} key={`obj${t.id}`}>
                        {t.name}
                      </div>
                    ) : null
                  )}
                </div>
              );
            },
          },
          {
            path: "goals",
            name: "Progression objectifs",
            render: (el) => (
              <>
                <div>{el.length} objectifs</div>
                {getCERGoalsProgress(el).map((t, tk) => (
                  <div style={{ fontSize: 11 }} title={t.name} key={`goa${t.id}${tk}`}>
                    <i className={`fa fa-${t.icon} text-${t.color} mr-2`} />
                    {t.count}
                  </div>
                ))}
              </>
            ),
          },
          {
            path: "userRecipient",
            name: "Référent unique",
            exportPath: "userManager",
            render: (el) => tools.getFullname(el.userManager),
          },
          {
            path: "*",
            name: "Localité",
            exportPath: "zone",
            render: (el) => (
              <>
                <span className="badge badge-dark">{el.zone}</span>
                <div>{tools.getFullAddress(el.userRecipient.location)}</div>
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default CERList;
